/*!
 * 
 * Angle - Bootstrap Admin App + AngularJS
 * 
 * Author: @themicon_co
 * Website: http://themicon.co
 * License: http://support.wrapbootstrap.com/knowledge_base/topics/usage-licenses
 * 
 */

if (typeof $ === 'undefined') { throw new Error('This application\'s JavaScript requires jQuery'); }

const CONFIG = {
  buildTime: '{{gulpVars_buildTime}}',
  bucket: '{{gulpVars_bucketName}}',
  generalBucket: '{{gulpVars_generalBucketName}}',
  baseUrl: '{{gulpVars_apiBaseURL}}',
  basicUrl: '{{gulpVars_basicURL}}',  
  functionsUrl: '{{gulpVars_functionsURL}}',
  appVersion: '{{gulpVars_versionString}}',
  stateGoTimeout: 500,
  firebaseConfig: {
    apiKey: '{{gulpVars_firebaseApiKey}}',
    authDomain: '{{gulpVars_firebaseAuthDomain}}',
    projectId: '{{gulpVars_firebaseProjectId}}',
    storageBucket: '{{gulpVars_firebaseStorageBucket}}',
    messagingSenderId: '{{gulpVars_firebaseMessagingSenderId}}',
    appId: '{{gulpVars_firebaseAppId}}'
  },
  folderSeparator: navigator && navigator.platform && navigator.platform.toLowerCase().indexOf('win')===0 ? '\\' : '/',
  folderRoot: navigator && navigator.platform && navigator.platform.toLowerCase().indexOf('win')===0 ? '' : '/'
}

if(CONFIG.appVersion=='local'){
  document.documentElement.style.filter = "hue-rotate(90deg)"
}
if(CONFIG.appVersion=='test'){
  document.documentElement.style.filter = "hue-rotate(40deg)"
}

class Logger {
  constructor(){
    this.T = Date.now()
    this._stack = {}
    if(CONFIG.appVersion=='local' || CONFIG.appVersion=='test'){
      this.log = this._log
      this.error = this._error
      this.stack = this._logStack
    } else {
      this.log = () => {}
      this.error = () => {}
      this.stack = () => { return { log: () => {}, error: () => {} } }
    }
  }

  _t(){
    return Date.now() - this.T
  }

  _log(){
    console.log(this._t(), ...arguments)
  }
  _error(){
    console.error(this._t(), ...arguments)
  }
  _logStack(key, entry, data){
    if(!this._stack[key]){
      this._stack[key] = []
    }
    this._stack[key].push(Object.assign({_t: this._t()}, { entry: entry, data:data }))
    return {
      log: (doNotReset) => {
        this._log(key, this._stack[key])
        if(!doNotReset) this._stack[key] = []
      },
      error: (doNotReset) => {
        this._error(key, this._stack[key])
        if(!doNotReset) this._stack[key] = []
      }
    }
  }
}

const L = new Logger()

class Firebase {

  constructor(){
    firebase.initializeApp(CONFIG.firebaseConfig);
    this.unsubcribers = {}
    this.token = null
    this.buffer = {}
    this.lastWrite = {}
    this.pending = {}
    this.timeouts = {}
    this.timestamps = {}
  }

  toTimestamp(date){
    return firebase.firestore.Timestamp.fromDate(date)
  }

  auth(token, cb){
    L.stack('FB.auth', 'call', token)
    let self = this
    if(!token) {
      firebase.auth().signOut().then(function() {
        L.stack('FB.auth', 'signed out').log(true)
        self.token = null
      }).catch(function(error) {
        L.stack('FB.auth', 'sign out error', error).error(true)
      });
    } else {
      firebase.auth().signInWithCustomToken(token)
        .then((userCredential) => {
          setTimeout(() => {
            L.stack('FB.auth', 'signed in', userCredential)
            this.getToken(true).then((idToken) => {
              setTimeout(() => {
                L.stack('FB.auth', 'token', idToken).log(true)
                if(cb)cb(idToken)
              }, 1000);
            })
        
          }, 1000);
        })
        .catch((error) => {
          L.stack('FB.auth', 'sign in error', error).error(true)
        });
    }
  }

  getToken(forceRefresh){
    L.stack('FB.auth', 'getToken', forceRefresh)
    let self = this
    return new Promise((resolve, reject) => {
      if(!firebase.auth().currentUser) {
        L.stack('FB.auth', 'no currentUser').error(true)
        reject('No user')
      }
      firebase.auth().currentUser.getIdToken(forceRefresh).then(function(idToken) {
        self.token = idToken
        L.stack('FB.auth', 'idToken', idToken).log(true)
        resolve(idToken)
      }).catch(function(error) {
        L.stack('FB.auth', 'getToken error', error).error(true)
        reject(error)
      })
    })
  }

  checkAuth(loggedInCallback, loggedOutCallback){
    let self = this
    firebase.auth().onAuthStateChanged(function(user) {
      L.stack('FB.auth', 'onAuthStateChanged', user)
      if (user) {
        self.getToken(true).then(function(idToken) {
          if(loggedInCallback)loggedInCallback()
        }).catch(function(error) {
          if(loggedOutCallback)loggedOutCallback()
        });
      } else {
        if(loggedOutCallback)loggedOutCallback()
      }
    })
  }

  get(type, id, cb, errCb){
    L.stack('FB.get.'+type+'.'+id, 'call')
    return firebase.firestore().collection(type).doc(id).get().then(function(doc){
      L.stack('FB.get.'+type+'.'+id, 'result', doc.data()).log()
      cb(doc.data());
      return doc;
    }).catch(function(error) {
      L.stack('FB.get.'+type+'.'+id, 'error', error).error()
      if(errCb){ errCb(error) }
      return null;
    })
  }

  ref(path){
    let ref = firebase.firestore();
    if(typeof path === 'string'){
      path = path.split('.')
    }
    path = path.map(p => p.split('/')).flat()
    for(let i=0; i<path.length; i++){
      if(i%2==0){
        ref = ref.collection(path[i])
      } else {
        ref = ref.doc(path[i])
      }
    }
    if(path.length%2==0){
      return ref
    } else {
      return ref.orderBy('__name__')
    }
  }

  pathToString(path){
    if(typeof path === 'string'){
      return path
    }
    return path.join('.')
  }

  resetBuffer(key){
    this.lastWrite[key] = Date.now()
    this.buffer[key] = []
  }

  streamWrite(self, key, path, data, cb, errCb, options){
    self.ref(path).set(data, {merge:true}).then(function(){
      L.stack('FB.stream.'+self.pathToString(path), 'write', {data: data.length}).log()
      self.resetBuffer(key)
      if(cb)cb();
    }).catch(function(error) {
      L.stack('FB.stream.'+self.pathToString(path), 'error', {data: data.length}).error()
      self.resetBuffer(key)
      if(errCb){ errCb(error) }
    })
  }

  stream(path, data, cb, errCb, options){
    L.stack('FB.stream.'+this.pathToString(path), 'call')
    options = options || {}
    let self = this
    let pathKey = this.pathToString(path)
    let lastWrite = this.lastWrite[pathKey] || 0
    let now = Date.now()
    let delta = now - lastWrite
    if(delta < 1000 || this.pending[pathKey]){
      L.stack('FB.stream.'+this.pathToString(path), 'buffer')
      this.buffer[pathKey] = this.buffer[pathKey] || []
      this.buffer[pathKey] = this.buffer[pathKey].concat(data.data || [])
      clearTimeout(this.timeouts[pathKey])
      this.pending[pathKey] = true
      this.timeouts[pathKey] = setTimeout(function(){
        L.stack('FB.stream.'+self.pathToString(path), 'timeout')
        self.pending[pathKey] = false
        data.data = self.buffer[pathKey]
        self.stream(path, data, cb, errCb, Object.assign({}, options, {lastWrite:now-lastWrite}))
      }, 1000 - (delta) > 0 ? 1000 - (delta) : 0)
    } else {
      this.ref(path).get().then(function(snapshot){
        if(!options.n) data.data = (data.data) || []
        else data.data = ((snapshot.data() && snapshot.data().data) || []).concat(data.data || [])
        L.stack('FB.stream.'+self.pathToString(path), 'get', { n: options.n, data: data.data.length })
        self.streamWrite(self, pathKey, path, data, cb, errCb, options)
      })
    }
  }

  streamCache(requestingUser, hash, data, cb, errCb, options){
    data.updates = firebase.firestore.FieldValue.increment(1);
    data.timestamp = firebase.firestore.FieldValue.serverTimestamp();
    this.stream(['cache', requestingUser, 'entries', hash], data, cb, errCb, options)
  }

  write(path, data, cb, errCb){
    L.stack('FB.write.'+this.pathToString(path), 'call')
    let self = this
    this.ref(path).set(data, {merge:true}).then(function(){
      L.stack('FB.write.'+self.pathToString(path), 'result', data).log()
      if(cb)cb();
    }).catch(function(error) {
      L.stack('FB.write.'+self.pathToString(path), 'error', error).error()
      if(errCb){ errCb(error) }
    })
  }

  writeCache(requestingUser, hash, data, cb, errCb){
    data.updates = firebase.firestore.FieldValue.increment(1);
    data.timestamp = firebase.firestore.FieldValue.serverTimestamp();
    L.stack('FB.write.'+this.pathToString(['cache', requestingUser, 'entries', hash]), 'writeCache')
    this.write(['cache', requestingUser, 'entries', hash], data, cb, errCb)
  }

  setUnsubscribe(path, unsubscribe){
    if(Array.isArray(path)){
      path = path.join('.')
    }
    this.unsubscribe(path);
    this.unsubcribers[path] = unsubscribe;
  }

  unsubscribe(path){
    if(Array.isArray(path)){
      path = path.join('.')
    }
    if(this.unsubcribers[path]){ 
      this.unsubcribers[path]() 
      delete this.unsubcribers[path]
    }
  }

  unsubscribeAll(){
    for(let path in this.unsubcribers){
      this.unsubscribe(path);
    }
  }

  read(path, cb, errCb, options){
    L.stack('FB.read.'+this.pathToString(path), 'call')
    options = options || {}
    let unsub = null;
    let self = this;
    if(options.doNotUnsubscribe && this.unsubcribers[path]===false){
      L.stack('FB.read.'+this.pathToString(path), 'alreadyListening')
      //attenzione c'è già una lettura in corso con un suo callback, non viene cambiato il callback
      return;
    }
    try{
      unsub = this.ref(path).onSnapshot(function(doc){
        let s = L.stack('FB.read.'+self.pathToString(path), 'result', doc.data ? doc.data() : doc.docs.length)
        if(doc.data && doc.data()===undefined){
          if(!options.noWorkaround){
            let to = setTimeout(() => { self.read(path, cb, errCb, options) }, options.interval || 30000)
            unsub = () => clearTimeout(to)
            fetch(CONFIG.baseUrl + '/fs/' + path.join(':').replace(/\//g,':')).then((resp) => {
              resp.json().then((data) => {
                cb(data.data)
                if(!options.keepOnListening && !options.doNotUnsubscribe){
                  self.unsubscribe(path);
                }
              })
            }).catch((err) => {
              if(unsub) self.unsubscribe(path);
              if(errCb){ errCb(err) }
            })
            if(!options.doNotUnsubscribe){
              self.setUnsubscribe(path, unsub)
            } else {
              self.setUnsubscribe(path, false)
            }
            return unsub
          } else {
            if(unsub) self.unsubscribe(path);
            if(errCb){ errCb(undefined) }
          }
        }else{
          cb(doc.data ? doc.data() : doc.docs.map(d => Object.assign({ Id: d.id }, d.data())));
          if(!options.keepOnListening && !options.doNotUnsubscribe){
            L.stack('FB.read.'+self.pathToString(path), 'unsubscribe').log()
            self.unsubscribe(path);
          } else {
            s.log(true)
          }
        }
      }, function(error){
        if(!options.noWorkaround){
          let to = setTimeout(() => { self.read(path, cb, errCb, options) }, options.interval || 30000)
          unsub = () => clearTimeout(to)
          fetch(CONFIG.baseUrl + '/fs/' + path.join(':').replace(/\//g,':')).then((resp) => {
            resp.json().then((data) => {
              cb(data.data)
              if(!options.keepOnListening && !options.doNotUnsubscribe){
                self.unsubscribe(path);
              }
            })
          }).catch((err) => {
            if(unsub) self.unsubscribe(path);
            if(errCb){ errCb(err) }
          })
          if(!options.doNotUnsubscribe){
            self.setUnsubscribe(path, unsub)
          } else {
            self.setUnsubscribe(path, false)
          }
          return unsub
        } else {
          if(unsub) self.unsubscribe(path);
          if(errCb){ errCb(error) }
        }
      })
      if(!options.doNotUnsubscribe){
        L.stack('FB.read.'+this.pathToString(path), 'setUnsubscribe', unsub)
        self.setUnsubscribe(path, unsub)
      } else {
        L.stack('FB.read.'+this.pathToString(path), 'setUnsubscribe', false)
        self.setUnsubscribe(path, false)
      }
      return unsub;
    } catch(e) {
      if(!options.noWorkaround){
        let to = setTimeout(() => { self.read(path, cb, errCb, options) }, options.interval || 30000)
        unsub = () => clearTimeout(to)
        fetch(CONFIG.baseUrl + '/fs/' + path.join(':').replace(/\//g,':')).then((resp) => {
          resp.json().then((data) => {
            cb(data.data)
            if(!options.keepOnListening && !options.doNotUnsubscribe){
              self.unsubscribe(path);
            }
          })
        }).catch((err) => {
          if(unsub) this.unsubscribe(path);
          if(errCb){ errCb(err) }
        })
        if(!options.doNotUnsubscribe){
          self.setUnsubscribe(path, unsub)
        } else {
          self.setUnsubscribe(path, false)
        }
        return unsub
      } else {
        L.stack('FB.read.'+this.pathToString(path), 'error', e).error()
        if(unsub) this.unsubscribe(path);
        if(errCb){ errCb(e) }
      }
    }
  }

  readCache(requestingUser, hash, cb, errCb, options){
    L.stack('FB.read.'+this.pathToString(['cache', requestingUser, 'entries', hash]), 'readCache')
    return this.read(['cache', requestingUser, 'entries', hash], cb, errCb, options)
  }

  listen(type, path, cb, errCb, options){
    options = options || {}
    if(options.noRT){
      L.stack('FB.read.'+this.pathToString([type, path]), 'listen')
      return this.read([type, path], cb, errCb, Object.assign(options, { keepOnListening: true }))
    } else {
      L.stack('FB.read.'+this.pathToString(['rt', type, 't', path]), 'listen')
      return this.read(['rt', type, 't', path], cb, errCb, Object.assign(options, { keepOnListening: true }))
    }
  }


  isAfter(t1,t2){
    return Math.floor(t1/1000) > Math.floor(t2/1000)
  }
}

const FIREBASE = new Firebase();



// APP START
// ----------------------------------- 

var App = angular.module('aqrate', ['ngRoute', 'ngAnimate', 'ngStorage', 'ngCookies', 'pascalprecht.translate', 'ui.bootstrap', 'ui.router', 'oc.lazyLoad', 'cfp.loadingBar', 'restangular', 'ngSanitize', 'tandibar/ng-rollbar', 'ngIdle', 'ui.router.state.events'])
          .run(["$rootScope", "$state", "$stateParams", "$window", '$templateCache', 'Idle', '$sce', '$filter', function ($rootScope, $state, $stateParams, $window, $templateCache, Idle, $sce, $filter) {
              // Set reference to access them from any scope
              $rootScope.$state = $state;
              $rootScope.$stateParams = $stateParams;
              $rootScope.$storage = $window.localStorage;
              
              $rootScope.$on('$stateChangeStart', function(event, toState, toParams, fromState, fromParams) {
                var state = toState;
                toState.params = toParams || {};
                FIREBASE.unsubscribeAll();
              });

              $rootScope.FB = FIREBASE;
              //$rootScope.$on('$stateChangeSuccess', function(event, toState, toParams, fromState, fromParams) {
                //L.log(event,toState, toParams, fromState, fromParams, window.location.href)
              //});

              $rootScope.changePage = function(params){
                window.location.href = window.location.href.replace(window.location.hash, '#/app/' + params.action + '/' + params.type + '/' + params.filter)
              }

              $rootScope.bucketName = function(c){
                if(!c){
                  return CONFIG.generalBucket
                }
                return CONFIG.bucket.replace('{{client}}', c)
              }
              // Scope Globals
              // ----------------------------------- 
              $rootScope.app = {
                name: 'Aqrate',
                description: '',//'Translation system',
                year: ''+((new Date()).getFullYear()),
                layout: {
                  isFixed: true,
                  isCollapsed: false,
                  isBoxed: false,
                  isRTL: false
                },
                viewAnimation: 'ng-fadeInUp',
                version: CONFIG.appVersion,
                messages: [],
                os: navigator.platform.toUpperCase().indexOf('MAC')>=0 ? 'mac' : 'win',
                config: CONFIG
              };
              $rootScope.user = {
                name:     '',
                role:      'GUEST',
                picture:  ''
              };
              $rootScope.now = new Date().getTime();
              Idle.watch();

              function detectIE() {
                var ua = window.navigator.userAgent;
              
                // Test values; Uncomment to check result …
              
                // IE 10
                // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';
                
                // IE 11
                // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';
                
                // Edge 12 (Spartan)
                // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';
                
                // Edge 13
                // ua = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';
              
                var msie = ua.indexOf('MSIE ');
                if (msie > 0) {
                  // IE 10 or older => return version number
                  return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
                }
              
                var trident = ua.indexOf('Trident/');
                if (trident > 0) {
                  // IE 11 => return version number
                  var rv = ua.indexOf('rv:');
                  return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
                }
              
                var edge = ua.indexOf('Edge/');
                if (edge > 0) {
                  // Edge (IE 12+) => return version number
                  return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
                }
              
                // other browser
                return false;
              }
              var version = detectIE();
              $rootScope.IE = version && version<12;
              $rootScope.isEdge = version && version>=12;

              $rootScope.addMessage = function(id, m){
                if(id=='version'){
                  m.type = 'header';
                  m.version = m.title.it_IT || m.title.en_GB || m.title.de_DE || CONFIG.appVersion;
                  var currentVersions = CONFIG.appVersion.split('.');
                  var updatedVersions = m.version.split('.');
                  m.show = m.show && m.version!=CONFIG.appVersion && (
                    ($rootScope.user.id && m.roles[$rootScope.user.groupType] && m.roles[$rootScope.user.superUser ? 'SUPERUSER' : 'USER'])
                  );
                  m.title = '';
                  m.message = $filter('translate')('labels.ALERTFRONTENDOUTDATED')
                } else if(id=='localversion'){
                  m.type = 'header';
                  m.version = m.version;
                  m.show = true;
                  m.title = '';
                  m.message = $filter('translate')('labels.ALERTLOCALOUTDATED')
                } else {
                  m.show = m.show && (
                    (!$rootScope.user.id && m.roles.GUEST) || 
                    ($rootScope.user.id && m.roles[$rootScope.user.groupType] && m.roles[$rootScope.user.superUser ? 'SUPERUSER' : 'USER'])
                  )
                  m.message = m.message[$rootScope.localeId || 'en_GB'];
                  m.title = m.title[$rootScope.localeId || 'en_GB'];
                }
                if(m.type=='overlay' && $rootScope.user.groupType=='ADMIN' && $rootScope.user.superUser) { m.show = false; }
                var content = '';
                if(m.type=='overlay'){
                  content = '<div class="well"><h4>'+m.title+'</h4><p>'+m.message+'</p></div>';
                } else {
                  content = (m.title ? '<h4>'+m.title+'</h4>' : '') + '<p>'+m.message+'</p>';
                }

                if(m.show && m.message){
                  var replaced = false;
                  for(var i=0; i<$rootScope.app.messages.length; i++){
                    if($rootScope.app.messages[i].id == id){
                      $rootScope.app.messages[i] = {
                        cls: 'alert alert-' + (m.cls || 'warning'),
                        content: $sce.trustAsHtml(content),
                        id: id,
                        type: m.type
                      }
                      replaced = true
                    }
                  }
                  if(!replaced){
                    $rootScope.app.messages.push({
                      cls: 'alert alert-' + (m.cls || 'warning'),
                      content: $sce.trustAsHtml(content),
                      id: id,
                      type: m.type
                    })
                  }
                }
              }

              $rootScope.updateFirebaseListeners = function(){
                FIREBASE.listen('app', 'messages', function(values){
                  $rootScope.app.messages = [];
                  for(var id in values){
                    $rootScope.addMessage(id, values[id])
                  }
                }, null, { doNotUnsubscribe: true, interval: 300000 })
              }

              $rootScope.updateFirebaseListeners();

            }

            
          ]);
